/* Storage bridge for vr.html, bundled to vrstore.js (iife). Exposes the same
   backends as the editor on window.PlayoutStore: device (localStorage) when
   signed out, Firestore when signed in — or, on a paired headset, read-only
   Firestore access to the paired account (anonymous auth + a grant doc; see
   cloud.ts "headset pairing"). Auth state is shared with the editor
   automatically (same origin, same Firebase persistence). */
import { DeviceBackend, seedDeviceStore, type LayoutMeta, type StoreBackend } from './store.ts';
import { floorsOf, projectOf } from './floors.ts';
import { CloudBackend, cloudReady, currentUser, deletePairCode, getGrant, initCloud, loadSharedLayout,
         onAuth, publishPairCode, resolveShare, signInAnonymouslyIfNeeded, unpairDevice, watchGrant,
         watchSharedLayout, type Grant } from './cloud.ts';

let backend: StoreBackend = new DeviceBackend();
let pairedTo: Grant | null = null;   // {uid, name} when this device is a paired headset
let unwatch: (() => void) | null = null;
const listeners: ((list: LayoutMeta[]) => void)[] = [];

function rewatch(): void {
  if (unwatch) unwatch();
  unwatch = backend.watchList(list => { for (const cb of listeners) cb(list); });
}

async function seededDevice(): Promise<StoreBackend> {
  const dev = new DeviceBackend();
  await seedDeviceStore(dev);
  return dev;
}

/** Human label for this device, shown in the editor's paired-headsets list. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/Quest 3S/i.test(ua)) return 'Meta Quest 3S';
  if (/Quest 3/i.test(ua)) return 'Meta Quest 3';
  if (/Quest 2/i.test(ua)) return 'Meta Quest 2';
  if (/Quest Pro/i.test(ua)) return 'Meta Quest Pro';
  if (/OculusBrowser|Quest/i.test(ua)) return 'Meta Quest';
  if (/iPad|iPhone/.test(ua)) return 'iOS browser';
  if (/Macintosh/.test(ua)) return 'Mac browser';
  if (/Windows/.test(ua)) return 'Windows browser';
  if (/Android/.test(ua)) return 'Android browser';
  return 'Browser';
}

const ready = new Promise<void>(resolve => {
  let first = true;
  initCloud().finally(() => {
    onAuth(async user => {
      if (user && !user.isAnonymous) {
        pairedTo = null;
        backend = new CloudBackend(user.uid);
      } else if (user && user.isAnonymous) {
        // paired headset? use the granted account read-only, else device store
        pairedTo = await getGrant(user.uid);
        backend = pairedTo ? new CloudBackend(pairedTo.uid) : await seededDevice();
      } else {
        pairedTo = null;
        backend = await seededDevice();
      }
      rewatch();
      if (first) { first = false; resolve(); }
    });
  });
});

(window as any).PlayoutStore = {
  ready,
  kind: () => backend.kind,
  /** multi-floor projects ("<project> · <floor>" layouts): the floors of the project a layout belongs to, bottom to top */
  floors: (name: string, names: string[]): string[] => { const p = projectOf(name); return p ? floorsOf(names, p.project) : []; },
  floorLabel: (name: string): string => projectOf(name)?.floor || name,
  list: () => backend.list(),
  load: (name: string) => backend.load(name),
  onList: (cb: (list: LayoutMeta[]) => void): (() => void) => {
    listeners.push(cb);
    return () => { const i = listeners.indexOf(cb); if (i >= 0) listeners.splice(i, 1); };
  },
  // share links: resolve pointer → load/watch the shared layout (no account needed)
  resolveShare,
  loadShared: loadSharedLayout,
  watchShared: watchSharedLayout,
  // headset pairing (see cloud.ts): code on the headset, typed in the editor
  pairing: {
    supported: () => cloudReady(),
    /** null = not paired; {uid, name} of the paired account otherwise. */
    paired: () => pairedTo,
    /** true when a real (non-anonymous) account is signed in here. */
    signedIn: () => { const u = currentUser(); return !!u && !u.isAnonymous; },
    /**
     * Begin pairing: anonymous sign-in, publish a code (→ onCode), then wait
     * for the grant. Resolves true once paired (backend already switched).
     * Returns false if `cancel` gets called first.
     */
    start(onCode: (code: string) => void): { done: Promise<boolean>; cancel: () => void } {
      let stop: (() => void) | null = null;
      let cancelled = false;
      const done = (async () => {
        const u = await signInAnonymouslyIfNeeded();
        const code = await publishPairCode(u.uid, deviceLabel());
        if (cancelled) { deletePairCode(code); return false; }
        onCode(code);
        const ok = await new Promise<boolean>(res => {
          stop = watchGrant(u.uid, grant => {
            if (!grant) return;
            if (stop) stop();
            pairedTo = grant;
            backend = new CloudBackend(grant.uid);
            rewatch();
            res(true);
          });
          if (cancelled) { stop(); res(false); }
        });
        deletePairCode(code);   // redeemed or abandoned — either way it's spent
        return ok;
      })();
      return { done, cancel: () => { cancelled = true; if (stop) stop(); } };
    },
    /** Forget the pairing: drop the grant, fall back to the device store. */
    async unpair(): Promise<void> {
      const u = currentUser();
      if (u && u.isAnonymous) await unpairDevice(u.uid);
      pairedTo = null;
      backend = await seededDevice();
      rewatch();
    },
  },
};
